import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CButton,
  CDataTable,
  CSpinner,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { ElectronicDocumentStatusHistory } from "../../models/electronic-document-status-history";
import { getElectronicDocumentHistory } from "../../api/electronic-document-history";
import { formatPygDateTime } from "../../utils/dates";
import ElectronicDocumentHistoryDetailModal from "./ElectronicDocumentHistoryDetailModal";
import ElectronicDocumentStatusBadge from "./ElectronicDocumentStatusBadge";

interface ElectronicDocumentHistoryListModalProps {
  show: boolean;
  invoiceId?: number;
  creditNoteId?: number;
  debitNoteId?: number;
  remissionNoteId?: number;
  autoInvoiceId?: number;
  receiptId?: number;
  cdc?: string;
  onCancel: () => void;
}

const ElectronicDocumentHistoryListModal: React.FC<
  ElectronicDocumentHistoryListModalProps
> = ({
  show,
  invoiceId,
  creditNoteId,
  debitNoteId,
  remissionNoteId,
  autoInvoiceId,
  receiptId,
  cdc,
  onCancel,
}) => {
  const [historyRecords, setHistoryRecords] = useState<
    ElectronicDocumentStatusHistory[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<
    ElectronicDocumentStatusHistory | undefined
  >(undefined);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);

  const fields = [
    { key: "createdAt", label: "Fecha", _classes: ["text-center"] },
    { key: "status", label: "Estado", _classes: ["text-center"] },
    { key: "cdc", label: "CDC", _classes: ["text-center"] },
    {
      key: "setResponseCode",
      label: "Código Respuesta",
      _classes: ["text-center"],
    },
    { key: "hasChanges", label: "Cambios", _classes: ["text-center"] },
    { key: "actions", label: "Acciones", _classes: ["text-center"] },
  ];

  const loadHistory = async () => {
    setLoading(true);
    try {
      const records = await getElectronicDocumentHistory({
        invoiceId,
        creditNoteId,
        debitNoteId,
        remissionNoteId,
        autoInvoiceId,
        receiptId,
        cdc,
      });
      setHistoryRecords(records);
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onClose = () => {
    setHistoryRecords([]);
    setShowDetailModal(false);
    setSelectedRecord(undefined);
    onCancel();
  };

  const onViewDetails = (record: ElectronicDocumentStatusHistory) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const onDetailModalClose = () => {
    setShowDetailModal(false);
    setSelectedRecord(undefined);
  };

  const documentTitle = invoiceId
    ? "Factura"
    : creditNoteId
      ? "Nota de Crédito"
      : debitNoteId
        ? "Nota de Débito"
        : remissionNoteId
          ? "Nota de Remisión"
          : autoInvoiceId
            ? "Autofactura"
            : receiptId
              ? "Recibo"
              : "Documento";

  return (
    <>
      <CModal show={show && !showDetailModal} onClose={onClose} size="xl">
        <CModalHeader closeButton>
          <CModalTitle>Historial de {documentTitle}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {loading ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <CDataTable
              items={historyRecords}
              fields={fields}
              hover
              striped
              border
              itemsPerPage={10}
              pagination
              scopedSlots={{
                createdAt: (item: ElectronicDocumentStatusHistory) => (
                  <td className="text-center">
                    {item.createdAt
                      ? formatPygDateTime(new Date(item.createdAt))
                      : "-"}
                  </td>
                ),
                status: (item: ElectronicDocumentStatusHistory) => (
                  <td className="text-center">
                    <ElectronicDocumentStatusBadge status={item.status} />
                  </td>
                ),
                cdc: (item: ElectronicDocumentStatusHistory) => (
                  <td className="text-center">
                    {item.cdc ? item.cdc.substring(0, 20) + "..." : "-"}
                  </td>
                ),
                setResponseCode: (item: ElectronicDocumentStatusHistory) => (
                  <td className="text-center">{item.setResponseCode || "-"}</td>
                ),
                hasChanges: (item: ElectronicDocumentStatusHistory) => (
                  <td className="text-center">
                    {item.changes && item.changes.length > 0
                      ? `${item.changes.length} campo(s)`
                      : "-"}
                  </td>
                ),
                actions: (item: ElectronicDocumentStatusHistory) => (
                  <td className="text-center">
                    <CButton
                      color="info"
                      size="sm"
                      onClick={() => onViewDetails(item)}
                    >
                      <i className="fa fa-eye"></i>
                    </CButton>
                  </td>
                ),
              }}
            />
          )}
        </CModalBody>
      </CModal>

      <ElectronicDocumentHistoryDetailModal
        show={showDetailModal}
        record={selectedRecord}
        onCancel={onDetailModalClose}
      />
    </>
  );
};

export default ElectronicDocumentHistoryListModal;
